import type { AdminMcpConnectionList, AdminMcpTeam, AdminMcpToken } from "@ternary/contracts"
import {
  Button,
  Card,
  type Column,
  ConfirmDialog,
  Field,
  IconButton,
  Input,
  Table,
} from "@ternary/ui"
import { type FormEvent, useState } from "react"
import { NavIcon } from "./nav-icons.js"

type Connection = AdminMcpConnectionList["connections"][number]

const DATE = new Intl.DateTimeFormat("en-US", { dateStyle: "medium" })
const date = (value: string | null) => (value ? DATE.format(new Date(value)) : "Never")
const addCommand = (url: string) => `claude mcp add --transport http ternary ${url}`

/** Copies to the clipboard, saying so (or how to copy by hand) in the returned notice. */
function useCopy() {
  const [notice, setNotice] = useState("")
  async function copy(text: string, copied: string) {
    try {
      await navigator.clipboard.writeText(text)
      setNotice(copied)
    } catch {
      setNotice("Select it and copy it.")
    }
  }
  return { copy, notice }
}

/**
 * Claude (MCP): the endpoint with Copy, and how to connect claude.ai, Claude Desktop and Claude
 * Code, as Claude's own screens and `claude mcp add` name things.
 */
export function McpConnectCard({ canConnect, url }: { canConnect: boolean; url: string }) {
  const { copy, notice } = useCopy()
  return (
    <Card title="Claude (MCP)">
      <div className="admin-stack">
        <p className="admin-note">
          Connect Claude to read your members, poll votes and saved posts. Claude can't change
          anything.
        </p>
        <span className="admin-url">
          <code className="admin-mono admin-url-text" title={url}>
            {url}
          </code>
          <IconButton
            icon={<NavIcon name="copy" />}
            label="Copy MCP URL"
            onClick={() => void copy(url, "MCP URL copied.")}
            size="sm"
          />
        </span>
        <p className="admin-note" role="status">
          {notice}
        </p>
        {canConnect ? null : (
          <p className="admin-note">Only owners and admins can connect Claude to this business.</p>
        )}
        <h3 className="admin-heading">claude.ai or Claude Desktop</h3>
        <ol className="admin-steps">
          <li>In Claude, open Customize → Connectors, choose + Add, then Add custom connector.</li>
          <li>Name it Ternary, paste the URL above and press Continue.</li>
          <li>Keep the sign-in Claude finds, choose Sign in now, and press Add.</li>
          <li>Ternary opens: sign in with your Ternary account and press Allow.</li>
          <li>In a chat, open + → Connectors and turn Ternary on.</li>
        </ol>
        <p className="admin-note">
          On a Team or Enterprise plan, an owner adds it first in Organization settings → Connectors
          (Add → Custom → Web). Then each person presses Connect next to it in Customize →
          Connectors.
        </p>
        <h3 className="admin-heading">Claude Code</h3>
        <p className="admin-note">
          Run this, then run /mcp in Claude Code and choose Authenticate:
        </p>
        <code className="admin-mono admin-command">{addCommand(url)}</code>
        <p className="admin-note">
          Or make a personal token below, for Claude Code or a script, and add it with --header.
        </p>
      </div>
    </Card>
  )
}

/**
 * The person's personal tokens: listed by name (never by value), each with Revoke after a
 * confirm, and a new one by name, which shows once with its Claude Code command. A read-only
 * account or a team member cannot make one.
 */
export function McpTokensCard({
  canConnect,
  creating,
  issuedToken,
  onCreateToken,
  onRevokeToken,
  revoking,
  tokens,
  url,
}: {
  canConnect: boolean
  /** Create token is on its way. */
  creating: boolean
  /** A token just created: its only showing. */
  issuedToken: string | null
  onCreateToken(label: string): void
  onRevokeToken(id: string): void
  /** A revoke is on its way. */
  revoking: boolean
  tokens: readonly AdminMcpToken[]
  url: string
}) {
  const { copy, notice } = useCopy()
  const [label, setLabel] = useState("")
  const [labelError, setLabelError] = useState<string>()
  const [asking, setAsking] = useState<AdminMcpToken | null>(null)

  function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const name = label.trim()
    if (!name) {
      setLabelError("Name the token, such as the computer it is for.")
      return
    }
    setLabelError(undefined)
    onCreateToken(name)
    setLabel("")
  }

  const columns: Column<AdminMcpToken>[] = [
    { cell: (token) => token.label, header: "Name", key: "name" },
    { cell: (token) => date(token.createdAt), header: "Created", key: "created", priority: 2 },
    { cell: (token) => date(token.lastUsedAt), header: "Last used", key: "used", priority: 2 },
    {
      align: "end",
      cell: (token) => (
        <Button
          aria-label={`Revoke ${token.label}`}
          onClick={() => setAsking(token)}
          size="sm"
          variant="secondary"
        >
          Revoke
        </Button>
      ),
      header: "Actions",
      key: "actions",
    },
  ]

  return (
    <>
      {issuedToken ? (
        <Card title="Copy this token now">
          <div className="admin-stack">
            <p className="admin-note">It won't be shown again.</p>
            <div className="admin-field-action">
              <Field label="New personal token">
                <Input className="admin-mono" readOnly value={issuedToken} />
              </Field>
              <Button onClick={() => void copy(issuedToken, "Token copied.")} variant="secondary">
                Copy token
              </Button>
            </div>
            <div className="admin-field-action">
              <Field label="Claude Code command">
                <Input
                  className="admin-mono"
                  readOnly
                  value={`${addCommand(url)} --header "Authorization: Bearer ${issuedToken}"`}
                />
              </Field>
              <Button
                onClick={() =>
                  void copy(
                    `${addCommand(url)} --header "Authorization: Bearer ${issuedToken}"`,
                    "Command copied.",
                  )
                }
                variant="secondary"
              >
                Copy command
              </Button>
            </div>
            <p className="admin-note" role="status">
              {notice}
            </p>
          </div>
        </Card>
      ) : null}
      <Card title="Personal tokens">
        <div className="admin-stack">
          <Table
            caption="Personal tokens"
            captionHidden
            columns={columns}
            empty="No personal tokens yet."
            rowKey={(token) => token.id}
            rows={[...tokens]}
          />
          {canConnect ? (
            <form className="admin-field-action" noValidate onSubmit={create}>
              <Field error={labelError} label="Token name">
                <Input
                  maxLength={100}
                  onChange={(event) => setLabel(event.target.value)}
                  placeholder="Work laptop"
                  value={label}
                />
              </Field>
              <Button loading={creating} type="submit" variant="secondary">
                Create token
              </Button>
            </form>
          ) : (
            <p className="admin-note">Only owners and admins can make a token.</p>
          )}
        </div>
      </Card>
      <ConfirmDialog
        body="Anything that uses this token stops working at once."
        confirmLabel="Revoke"
        danger
        onCancel={() => setAsking(null)}
        onConfirm={() => {
          if (asking) onRevokeToken(asking.id)
          setAsking(null)
        }}
        open={asking !== null}
        pending={revoking}
        title={`Revoke ${asking?.label ?? "this token"}?`}
      />
    </>
  )
}

/** The apps the person connected through Claude's sign-in, each with Revoke after a confirm. */
export function McpConnectionsCard({
  connections,
  onRevokeConnection,
  revoking,
}: {
  connections: readonly Connection[]
  onRevokeConnection(id: string): void
  /** A revoke is on its way. */
  revoking: boolean
}) {
  const [asking, setAsking] = useState<Connection | null>(null)
  const columns: Column<Connection>[] = [
    { cell: (connection) => connection.clientName, header: "App", key: "app" },
    {
      cell: (connection) => date(connection.connectedAt),
      header: "Connected",
      key: "connected",
      priority: 2,
    },
    {
      cell: (connection) => date(connection.lastUsedAt),
      header: "Last used",
      key: "used",
      priority: 2,
    },
    {
      align: "end",
      cell: (connection) => (
        <Button
          aria-label={`Revoke ${connection.clientName}`}
          onClick={() => setAsking(connection)}
          size="sm"
          variant="secondary"
        >
          Revoke
        </Button>
      ),
      header: "Actions",
      key: "actions",
    },
  ]
  return (
    <>
      <Card title="Connected apps">
        <Table
          caption="Connected apps"
          captionHidden
          columns={columns}
          empty="No apps connected yet."
          rowKey={(connection) => connection.id}
          rows={[...connections]}
        />
      </Card>
      <ConfirmDialog
        body="It stops reading your data at once. You can connect it again later."
        confirmLabel="Disconnect"
        danger
        onCancel={() => setAsking(null)}
        onConfirm={() => {
          if (asking) onRevokeConnection(asking.id)
          setAsking(null)
        }}
        open={asking !== null}
        pending={revoking}
        title={`Disconnect ${asking?.clientName ?? "this app"}?`}
      />
    </>
  )
}

/** One teammate's token or connected app, as the team card lists it. */
interface TeamAccess {
  adminId: string
  email: string
  id: string
  kind: "token" | "connection"
  lastUsedAt: string | null
  what: string
}

/**
 * For owners: every other person's live personal tokens and connected apps in the business, each
 * with Revoke after a confirm, so an owner can end a teammate's Claude access.
 */
export function McpTeamCard({
  onRevoke,
  people,
  revoking,
}: {
  onRevoke(access: { adminId: string; id: string; kind: "token" | "connection" }): void
  people: AdminMcpTeam["people"]
  /** A revoke is on its way. */
  revoking: boolean
}) {
  const [asking, setAsking] = useState<TeamAccess | null>(null)
  const rows: TeamAccess[] = people.flatMap(({ adminId, connections, email, tokens }) => [
    ...tokens.map(({ id, label, lastUsedAt }) => ({
      adminId,
      email,
      id,
      kind: "token" as const,
      lastUsedAt,
      what: `Token: ${label}`,
    })),
    ...connections.map(({ clientName, id, lastUsedAt }) => ({
      adminId,
      email,
      id,
      kind: "connection" as const,
      lastUsedAt,
      what: `App: ${clientName}`,
    })),
  ])
  const columns: Column<TeamAccess>[] = [
    { cell: (access) => access.email || "A teammate", header: "Person", key: "person" },
    { cell: (access) => access.what, header: "Access", key: "access" },
    { cell: (access) => date(access.lastUsedAt), header: "Last used", key: "used", priority: 2 },
    {
      align: "end",
      cell: (access) => (
        <Button
          aria-label={`Revoke ${access.email || "a teammate"}'s ${access.what}`}
          onClick={() => setAsking(access)}
          size="sm"
          variant="secondary"
        >
          Revoke
        </Button>
      ),
      header: "Actions",
      key: "actions",
    },
  ]
  return (
    <>
      <Card title="Your team's Claude access">
        <Table
          caption="Your team's Claude access"
          captionHidden
          columns={columns}
          empty="Nobody else on your team has connected Claude."
          rowKey={(access) => `${access.kind}:${access.id}`}
          rows={rows}
        />
      </Card>
      <ConfirmDialog
        body="It stops reading your data at once."
        confirmLabel="Revoke"
        danger
        onCancel={() => setAsking(null)}
        onConfirm={() => {
          if (asking) onRevoke({ adminId: asking.adminId, id: asking.id, kind: asking.kind })
          setAsking(null)
        }}
        open={asking !== null}
        pending={revoking}
        title={`End ${asking?.email || "a teammate"}'s ${asking?.what ?? "access"}?`}
      />
    </>
  )
}

/**
 * Settings → Developer → Claude (MCP), while the deployment offers it: how to connect, the
 * person's personal tokens and the apps they connected. It only shows: the page creates and
 * revokes through the callbacks.
 */
export function McpSettings({
  canConnect,
  connections,
  creating,
  issuedToken,
  onCreateToken,
  onRevokeConnection,
  onRevokeToken,
  revoking,
  tokens,
  url,
}: {
  canConnect: boolean
  connections: readonly Connection[]
  creating: boolean
  issuedToken: string | null
  onCreateToken(label: string): void
  onRevokeConnection(id: string): void
  onRevokeToken(id: string): void
  revoking: boolean
  tokens: readonly AdminMcpToken[]
  /** The MCP endpoint, `${ADMIN_PUBLIC_URL}/mcp`. */
  url: string
}) {
  return (
    <>
      <McpConnectCard canConnect={canConnect} url={url} />
      <McpTokensCard
        canConnect={canConnect}
        creating={creating}
        issuedToken={issuedToken}
        onCreateToken={onCreateToken}
        onRevokeToken={onRevokeToken}
        revoking={revoking}
        tokens={tokens}
        url={url}
      />
      <McpConnectionsCard
        connections={connections}
        onRevokeConnection={onRevokeConnection}
        revoking={revoking}
      />
    </>
  )
}
